import React, { useRef, useEffect, useState } from "react";
import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental } from "vexflow";
import { buildScalePattern, noteToSemitones, getNoteAtPosition } from "../lib/guitarEngine";
import type { NoteName } from "../lib/guitarEngine";
import { PEDAGOGICAL_DEGREE_HEX } from "../lib/scaleModuleTheme";
import {
  PRACTICE_STAFF_HEIGHT_TREBLE,
  PRACTICE_STAFF_STAVE_SCALE,
} from "../lib/practiceStaffLayout";
import {
  PRACTICE_STAFF_COLORS,
  PRACTICE_STAFF_COLORS_ATMOSPHERIC,
  PRACTICE_STAFF_CONTAINER_STYLE,
  notationNoteStyle,
  paintStaveLines,
  serenityStaffDropShadow,
} from "../lib/practiceStaffTheme";

export type StaffFilterMode = "all" | "tonic" | "triad" | "pentatonic" | "octave";

interface StaffVisualizerProps {
  rootNote: NoteName;
  scaleType: string;
  activeNote?: { string: number; fret: number } | null;
  filterMode?: StaffFilterMode;
  highlightNote?: NoteName | null;
  playbackSequence?: Array<{ note: NoteName; string: number; fret: number }>;
  currentSeqIndex?: number;
  /** Alineado con SongLearnCanvasBackdrop (texto claro + tarjeta cristal). */
  learnSurface?: boolean;
  /** Widget compacto (Explore): sin título ni leyenda, altura reducida */
  compact?: boolean;
  /** Marca de agua teórica (Explore sandbox) */
  atmospheric?: boolean;
  /** Solo escala estática: no registrar notas tocadas ni cambiar de vista */
  staticScaleOnly?: boolean;
  /** Explore: notas del patrón / escala distribuidas en X */
  staffNotePositions?: StaffDisplayedNote[];
  hoverNote?: { string: number; fret: number } | null;
}

interface PlayedNote {
  note: NoteName;
  string: number;
  fret: number;
  id: number;
}

/** Guitarra se escribe una octava arriba del sonido real para que quede legible en clave de sol 8vb */
const GUITAR_TRANSPOSITION = 12;
const NOTES_PER_MEASURE = 4;
const MEASURE_WIDTH = 320;
const STAVE_SCALE = PRACTICE_STAFF_STAVE_SCALE;
const STAFF_RENDER_HEIGHT = PRACTICE_STAFF_HEIGHT_TREBLE;
/** Explore: lienzo sin recorte — 5 líneas + clave 8vb visibles */
const COMPACT_STAVE_SCALE = 1.05;
const COMPACT_STAFF_HEIGHT = 108;
const PADDING_X = 24;

function finalizeCompactStaffSvg(container: HTMLDivElement, canvasW: number, canvasH: number) {
  const svg = container.querySelector("svg");
  if (!svg) return;
  svg.setAttribute("viewBox", `0 0 ${canvasW} ${canvasH}`);
  svg.setAttribute("preserveAspectRatio", "xMinYMid meet");
  svg.style.width = "100%";
  svg.style.height = "auto";
  svg.style.display = "block";
  svg.style.overflow = "visible";
}

/** Halo Serenity por grado en cabezas VexFlow (sin discos en canvas). */
function applyExploreStaffNoteHeadGlow(
  container: HTMLDivElement,
  staffNotes: Array<{ isActive: boolean; isHighlighted: boolean; degree: string }>
) {
  const svg = container.querySelector("svg");
  if (!svg) return;
  const noteGroups = Array.from(svg.querySelectorAll(".vf-stavenote"));
  noteGroups.forEach((group, i) => {
    const sn = staffNotes[i];
    if (!sn || (!sn.isActive && !sn.isHighlighted)) return;
    const el = group as SVGGElement;
    el.style.transition = "filter 300ms ease-out";
    if (sn.isActive) {
      el.style.filter = serenityStaffDropShadow(sn.degree, true);
    } else if (sn.isHighlighted) {
      el.style.filter = serenityStaffDropShadow(sn.degree, false);
    }
  });
}

/** Una sola nota activa: mismo MIDI escrito + misma grafía (evita Fa#/Solb a la vez). */
function matchesActiveStaffNote(
  sn: { displayMidi: number; note: NoteName },
  activeNote: { string: number; fret: number } | null | undefined
): boolean {
  if (!activeNote) return false;
  const pos = getNoteAtPosition(activeNote.string, activeNote.fret);
  const writtenMidi = pos.midi + GUITAR_TRANSPOSITION;
  if (writtenMidi !== sn.displayMidi) return false;
  return pos.note === sn.note;
}

function matchesSeqStaffNote(
  sn: { displayMidi: number; note: NoteName },
  playbackSequence: StaffVisualizerProps["playbackSequence"],
  currentSeqIndex: number
): boolean {
  if (!playbackSequence || currentSeqIndex < 0 || currentSeqIndex >= playbackSequence.length) {
    return false;
  }
  const seq = playbackSequence[currentSeqIndex];
  const pos = getNoteAtPosition(seq.string, seq.fret);
  const writtenMidi = pos.midi + GUITAR_TRANSPOSITION;
  if (writtenMidi !== sn.displayMidi) return false;
  return pos.note === seq.note;
}

function buildVexKey(note: NoteName, octave: number): string {
  const baseName = note.replace("#", "").replace("b", "").toLowerCase();
  return `${baseName}/${octave}`;
}

function degreeHaloColor(degree: string, isActive: boolean, isHighlighted: boolean): string {
  const P = PEDAGOGICAL_DEGREE_HEX;
  if (isActive) return "rgba(245, 158, 11, 0.35)";
  if (isHighlighted) return "rgba(251, 191, 36, 0.32)";
  if (degree === "R") return `${P[0]}44`;
  if (degree === "3" || degree === "b3") return `${P[2]}44`;
  if (degree === "5") return `${P[4]}44`;
  return "rgba(240, 243, 245, 0.12)";
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODO ESCALA — renderiza la escala completa estática
// ═══════════════════════════════════════════════════════════════════════════════
function renderScaleMode(
  container: HTMLDivElement,
  rootNote: NoteName,
  scaleType: string,
  filterMode: StaffFilterMode,
  activeNote: StaffVisualizerProps["activeNote"],
  playbackSequence: StaffVisualizerProps["playbackSequence"],
  currentSeqIndex: number,
  compact = false,
  atmospheric = false
) {
  container.innerHTML = "";

  const palette = atmospheric ? PRACTICE_STAFF_COLORS_ATMOSPHERIC : PRACTICE_STAFF_COLORS;
  const width = container.clientWidth || 800;
  const height = compact ? COMPACT_STAFF_HEIGHT : STAFF_RENDER_HEIGHT;
  const staveScale = compact ? COMPACT_STAVE_SCALE : STAVE_SCALE;

  const renderer = new Renderer(container, Renderer.Backends.SVG);
  renderer.resize(width, height);

  const context = renderer.getContext();
  context.scale(staveScale, staveScale);

  const staveX = PADDING_X / staveScale;
  const staveY = compact ? 22 : 10;
  const staveWidth = (width - PADDING_X * 2) / staveScale;

  const stave = new Stave(staveX, staveY, staveWidth);
  stave.addClef("treble", "default", "8vb");
  paintStaveLines(context, palette);
  stave.setContext(context).draw();

  const pattern = buildScalePattern(rootNote, scaleType);
  const notes = pattern.notes;
  const degrees = pattern.degrees;
  if (notes.length === 0) return;

  const rootSemitone = noteToSemitones(rootNote);
  const baseMidi = 48 + rootSemitone;

  let staffNotes: Array<{
    key: string;
    note: NoteName;
    degree: string;
    displayMidi: number;
    isActive: boolean;
    isHighlighted: boolean;
  }> = [];

  if (filterMode === "tonic") {
    [4, 5, 6].forEach((oct) => {
      const displayMidi = baseMidi + (oct - 4) * 12 + GUITAR_TRANSPOSITION;
      staffNotes.push({
        key: buildVexKey(rootNote, oct),
        note: rootNote,
        degree: "R",
        displayMidi,
        isActive: matchesActiveStaffNote({ displayMidi, note: rootNote }, activeNote),
        isHighlighted: false,
      });
    });
  } else if (filterMode === "triad") {
    [0, 2, 4].forEach((i) => {
      if (i >= notes.length) return;
      const note = notes[i];
      const degree = degrees[i] || "";
      const semitone = noteToSemitones(note);
      const interval = ((semitone - rootSemitone) % 12 + 12) % 12;
      const displayMidi = baseMidi + interval + GUITAR_TRANSPOSITION;
      const octave = Math.floor(displayMidi / 12) - 1;
      staffNotes.push({
        key: buildVexKey(note, octave),
        note,
        degree,
        displayMidi,
        isActive: matchesActiveStaffNote({ displayMidi, note }, activeNote),
        isHighlighted: matchesSeqStaffNote({ displayMidi, note }, playbackSequence, currentSeqIndex),
      });
    });
  } else if (filterMode === "pentatonic") {
    const pentatonicIntervals = scaleType.includes("minor")
      ? [0, 3, 5, 7, 10]
      : [0, 2, 4, 7, 9];
    pentatonicIntervals.forEach((interval) => {
      const noteIdx = notes.findIndex((_, idx) => {
        const s = noteToSemitones(notes[idx]);
        const rootS = noteToSemitones(rootNote);
        return ((s - rootS) % 12 + 12) % 12 === interval;
      });
      if (noteIdx === -1) return;
      const note = notes[noteIdx];
      const degree = degrees[noteIdx] || "";
      const displayMidi = baseMidi + interval + GUITAR_TRANSPOSITION;
      const octave = Math.floor(displayMidi / 12) - 1;
      staffNotes.push({
        key: buildVexKey(note, octave),
        note,
        degree,
        displayMidi,
        isActive: matchesActiveStaffNote({ displayMidi, note }, activeNote),
        isHighlighted: matchesSeqStaffNote({ displayMidi, note }, playbackSequence, currentSeqIndex),
      });
    });
  } else {
    const maxNotes = filterMode === "octave" ? Math.min(notes.length, 8) : notes.length;
    for (let i = 0; i < maxNotes; i++) {
      const note = notes[i];
      const degree = degrees[i] || "";
      const semitone = noteToSemitones(note);
      const interval = ((semitone - rootSemitone) % 12 + 12) % 12;
      const displayMidi = baseMidi + interval + GUITAR_TRANSPOSITION;
      const octave = Math.floor(displayMidi / 12) - 1;
      staffNotes.push({
        key: buildVexKey(note, octave),
        note,
        degree,
        displayMidi,
        isActive: matchesActiveStaffNote({ displayMidi, note }, activeNote),
        isHighlighted: matchesSeqStaffNote({ displayMidi, note }, playbackSequence, currentSeqIndex),
      });
    }
  }

  const vexNotes = staffNotes.map((sn) => {
    const staveNote = new StaveNote({
      keys: [sn.key],
      duration: "q",
      autoStem: true,
    });
    if (sn.note.includes("#")) staveNote.addModifier(new Accidental("#"), 0);
    else if (sn.note.includes("b") && sn.note.length > 1) staveNote.addModifier(new Accidental("b"), 0);

    staveNote.setStyle(
      notationNoteStyle(
        {
          active: sn.isActive,
          highlighted: sn.isHighlighted,
          degree: sn.degree,
          atmospheric,
        },
        palette
      )
    );
    return staveNote;
  });

  if (vexNotes.length === 0) return;

  const voice = new Voice({ numBeats: vexNotes.length, beatValue: 4 });
  voice.addTickables(vexNotes);
  new Formatter().joinVoices([voice]).format([voice], staveWidth - 50);

  const tickables = voice.getTickables() as InstanceType<typeof StaveNote>[];
  tickables.forEach((t) => t.setStave(stave));

  if (!atmospheric) {
    tickables.forEach((tickable, i) => {
      const sn = staffNotes[i];
      if (!sn || (!sn.isActive && !sn.isHighlighted)) return;
      let bbox;
      try {
        bbox = tickable.getBoundingBox();
      } catch {
        return;
      }
      if (!bbox) return;
      const cx = bbox.getX() + bbox.getW() / 2;
      const cy = bbox.getY() + bbox.getH() / 2;
      const halo = degreeHaloColor(sn.degree, sn.isActive, sn.isHighlighted);
      context.save();
      context.setFillStyle(halo);
      context.beginPath();
      context.arc(cx, cy, sn.isActive ? 14 : 18, 0, Math.PI * 2);
      context.fill();
      context.restore();
    });
  }

  voice.draw(context, stave);

  if (atmospheric) {
    applyExploreStaffNoteHeadGlow(container, staffNotes);
  }

  if (compact) {
    finalizeCompactStaffSvg(container, width, height);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// MODO NOTAS TOCADAS — secuencia horizontal con compases y scroll
// ═══════════════════════════════════════════════════════════════════════════════
function renderPlayedMode(
  container: HTMLDivElement,
  playedNotes: PlayedNote[]
) {
  container.innerHTML = "";
  if (playedNotes.length === 0) return;

  const containerWidth = container.clientWidth || 800;
  const innerWidth = containerWidth - PADDING_X * 2;
  const measureWidth = innerWidth / 4;
  const totalWidth = containerWidth;
  const height = STAFF_RENDER_HEIGHT;

  const renderer = new Renderer(container, Renderer.Backends.SVG);
  renderer.resize(totalWidth * STAVE_SCALE, height);
  const context = renderer.getContext();
  context.scale(STAVE_SCALE, STAVE_SCALE);

  // 4 compases fijos de 4 negras = 16 espacios (buffer circular)
  const measures: (PlayedNote | null)[][] = [[], [], [], []];
  for (let i = 0; i < 16; i++) {
    measures[Math.floor(i / 4)][i % 4] = playedNotes[i] || null;
  }

  measures.forEach((measureNotes, measureIndex) => {
    const staveX = (PADDING_X + measureIndex * measureWidth) / STAVE_SCALE;
    const staveY = 10;
    const staveW = measureWidth / STAVE_SCALE;
    const stave = new Stave(staveX, staveY, staveW);

    if (measureIndex === 0) {
      stave.addClef("treble", "default", "8vb");
    }
    stave.setEndBarType(measureIndex === 3 ? 3 : 1);
    paintStaveLines(context);
    stave.setContext(context).draw();

    // Construir 4 posiciones: notas reales + silencios transparentes
    const vexNotes = measureNotes.map((pn, noteIndex) => {
      if (!pn) {
        // Silencio invisible para mantener la métrica del compás
        const rest = new StaveNote({ keys: ["b/4"], duration: "qr" });
        rest.setStyle({
          fillStyle: PRACTICE_STAFF_COLORS.rest,
          strokeStyle: PRACTICE_STAFF_COLORS.rest,
        });
        return rest;
      }
      const pos = getNoteAtPosition(pn.string, pn.fret);
      const displayMidi = pos.midi + GUITAR_TRANSPOSITION;
      const octave = Math.floor(displayMidi / 12) - 1;
      const key = buildVexKey(pos.note, octave);

      const staveNote = new StaveNote({
        keys: [key],
        duration: "q",
        autoStem: true,
      });
      if (pos.note.includes("#")) staveNote.addModifier(new Accidental("#"), 0);
      else if (pos.note.includes("b") && pos.note.length > 1) staveNote.addModifier(new Accidental("b"), 0);

      const isLast = measureIndex === 3 && noteIndex === 3;
      staveNote.setStyle(notationNoteStyle({ active: isLast }));
      return staveNote;
    });

    const voice = new Voice({ numBeats: 4, beatValue: 4 });
    voice.addTickables(vexNotes);
    new Formatter().joinVoices([voice]).format([voice], staveW - 30);
    voice.draw(context, stave);
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export const StaffVisualizer: React.FC<StaffVisualizerProps> = ({
  rootNote,
  scaleType,
  activeNote,
  filterMode = "all",
  playbackSequence,
  currentSeqIndex = -1,
  learnSurface = false,
  compact = false,
  atmospheric = false,
  staticScaleOnly = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [playedNotes, setPlayedNotes] = useState<PlayedNote[]>([]);
  const [viewMode, setViewMode] = useState<'scale' | 'played'>('scale');
  const lastActiveKeyRef = useRef<string | null>(null);

  // ─── Detectar notas tocadas en el mástil ───
  useEffect(() => {
    if (staticScaleOnly) return;
    if (!activeNote) return;

    const key = `${activeNote.string}-${activeNote.fret}`;
    if (lastActiveKeyRef.current === key) return; // evitar repeticiones
    lastActiveKeyRef.current = key;

    const pos = getNoteAtPosition(activeNote.string, activeNote.fret);
    setPlayedNotes((prev) => {
      const next = [...prev, {
        note: pos.note,
        string: activeNote.string,
        fret: activeNote.fret,
        id: Date.now(),
      }];
      if (next.length > 16) return next.slice(-16);
      return next;
    });
    setViewMode('played');
  }, [activeNote, staticScaleOnly]);

  // ─── Renderizado ───
  useEffect(() => {
    if (!containerRef.current) return;

    let resizeTimeout: ReturnType<typeof setTimeout>;

    const render = () => {
      if (!containerRef.current) return;
      const showScale = staticScaleOnly || viewMode === 'scale';
      if (showScale) {
        renderScaleMode(
          containerRef.current,
          rootNote,
          scaleType,
          filterMode,
          activeNote,
          staticScaleOnly ? undefined : playbackSequence,
          staticScaleOnly ? -1 : currentSeqIndex,
          compact,
          atmospheric
        );
      } else {
        renderPlayedMode(containerRef.current, playedNotes);
      }
    };

    const debouncedRender = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(render, 100);
    };

    render();

    const observer = new ResizeObserver(debouncedRender);
    observer.observe(containerRef.current);

    return () => {
      clearTimeout(resizeTimeout);
      observer.disconnect();
    };
  }, [rootNote, scaleType, filterMode, viewMode, playedNotes, activeNote, playbackSequence, currentSeqIndex, compact, atmospheric, staticScaleOnly]);

  const title = viewMode === 'played'
    ? "Modo práctica"
    : filterMode === "tonic"
    ? "Tónicas en el pentagrama"
    : filterMode === "triad"
    ? "Acorde de tónica (I · III · V)"
    : filterMode === "pentatonic"
    ? "Pentagrama pentatónico"
    : filterMode === "octave"
    ? "Una octava completa"
    : "Pentagrama de la escala";

  return (
    <div className={`w-full ${compact ? "" : ""}`}>
      {!compact && (
      <div className="flex items-center justify-between mb-2">
        <h3 className={`text-sm font-semibold font-sans ${learnSurface ? "text-slate-200" : "text-slate-200"}`}>
          {title}
        </h3>
        {viewMode === 'played' && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setPlayedNotes([]);
                lastActiveKeyRef.current = null;
              }}
              className={
                learnSurface
                  ? "text-[10px] font-sans text-slate-300 hover:text-rose-300 transition-colors px-2 py-1 rounded bg-white/10 hover:bg-white/15 border border-white/10"
                  : "text-[10px] font-sans text-[#8A827A] hover:text-[#D45D79] transition-colors px-2 py-1 rounded bg-[#F5F0E8] hover:bg-[#F5E0E0]"
              }
            >
              Limpiar
            </button>
            <button
              onClick={() => {
                setViewMode('scale');
                setPlayedNotes([]);
                lastActiveKeyRef.current = null;
              }}
              className={
                learnSurface
                  ? "text-[10px] font-sans text-slate-300 hover:text-slate-100 transition-colors px-2 py-1 rounded bg-white/10 hover:bg-white/15 border border-white/10"
                  : "text-[10px] font-sans text-[#8A827A] hover:text-[#5A524A] transition-colors px-2 py-1 rounded bg-[#F5F0E8] hover:bg-[#E8E0D4]"
              }
            >
              Ver escala
            </button>
          </div>
        )}
      </div>
      )}
      <div
        className="w-full overflow-visible"
        style={{
          ...PRACTICE_STAFF_CONTAINER_STYLE,
          minHeight: compact ? undefined : STAFF_RENDER_HEIGHT,
        }}
      >
        <div
          ref={containerRef}
          className={
            compact
              ? "w-full h-auto [&_svg]:!h-auto [&_svg]:!max-h-none [&_svg]:w-full"
              : undefined
          }
        />
      </div>
      {!compact && (
      <div className={`flex items-center gap-4 mt-2 text-[10px] font-sans flex-wrap ${learnSurface ? "text-slate-400" : "text-[#8A827A]"}`}>
        {viewMode === 'played' ? (
          <>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#F59E0B] inline-block" />
              Última nota
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#F0F3F5] inline-block opacity-70" />
              Notas previas
            </span>
          </>
        ) : (
          <>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full inline-block opacity-80" style={{ backgroundColor: PEDAGOGICAL_DEGREE_HEX[0] }} />
              Tónica (halo)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full inline-block opacity-80" style={{ backgroundColor: PEDAGOGICAL_DEGREE_HEX[2] }} />
              3ª (halo)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full inline-block opacity-80" style={{ backgroundColor: PEDAGOGICAL_DEGREE_HEX[4] }} />
              5ª (halo)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#F59E0B] inline-block" />
              Activa (tocada)
            </span>
          </>
        )}
      </div>
      )}
    </div>
  );
};

export default StaffVisualizer;
